type PoiLike = {
  id?: unknown;
  _id?: unknown;
  assembly?: { id?: string | null } | null;
  assemblyId?: string | null;
};

const extractPoiId = (poi: PoiLike): string => {
  if (poi?.id !== undefined && poi?.id !== null) return String(poi.id);
  if (poi?._id !== undefined && poi?._id !== null) return String(poi._id);
  return '';
};

const extractAssemblyId = (poi: PoiLike): string =>
  poi?.assemblyId?.toString() ?? poi?.assembly?.id?.toString() ?? '';

export const computeReviewActionPoiIds = (
  reviewActionPois: unknown,
  assemblyOrder: string[],
  poiOrder: Record<string, string[]>
): Record<string, string[]> => {
  if (!reviewActionPois || typeof reviewActionPois !== 'object') return {};

  const assemblyIndex = new Map(assemblyOrder.map((id, index) => [id, index]));
  const poiIndexByAssembly = new Map<string, Map<string, number>>();
  for (const [assemblyId, ids] of Object.entries(poiOrder ?? {})) {
    poiIndexByAssembly.set(assemblyId, new Map(ids.map((id, index) => [id, index])));
  }

  const result: Record<string, string[]> = {};
  for (const [reviewAction, pois] of Object.entries(
    reviewActionPois as Record<string, PoiLike[]>
  )) {
    if (!Array.isArray(pois) || pois.length === 0) {
      result[reviewAction] = [];
      continue;
    }

    const sorted = [...pois].sort((a, b) => {
      const aAssemblyId = extractAssemblyId(a);
      const bAssemblyId = extractAssemblyId(b);
      const aAssemblyIdx = assemblyIndex.get(aAssemblyId) ?? Number.MAX_SAFE_INTEGER;
      const bAssemblyIdx = assemblyIndex.get(bAssemblyId) ?? Number.MAX_SAFE_INTEGER;
      if (aAssemblyIdx !== bAssemblyIdx) return aAssemblyIdx - bAssemblyIdx;

      const aPoiIdx = poiIndexByAssembly.get(aAssemblyId)?.get(extractPoiId(a));
      const bPoiIdx = poiIndexByAssembly.get(bAssemblyId)?.get(extractPoiId(b));
      const aFinal = aPoiIdx ?? Number.MAX_SAFE_INTEGER;
      const bFinal = bPoiIdx ?? Number.MAX_SAFE_INTEGER;
      return aFinal - bFinal;
    });

    result[reviewAction] = sorted.map(extractPoiId).filter(Boolean);
  }

  return result;
};

const buildPoiComparator = (assemblyOrder: string[], poiOrder: Record<string, string[]>) => {
  const assemblyIndex = new Map(assemblyOrder.map((id, index) => [id, index]));
  const poiIndexByAssembly = new Map<string, Map<string, number>>();
  for (const [assemblyId, ids] of Object.entries(poiOrder ?? {})) {
    poiIndexByAssembly.set(assemblyId, new Map(ids.map((id, index) => [id, index])));
  }

  return (a: PoiLike, b: PoiLike) => {
    const aAssemblyId = extractAssemblyId(a);
    const bAssemblyId = extractAssemblyId(b);
    const aAssemblyIdx = assemblyIndex.get(aAssemblyId) ?? Number.MAX_SAFE_INTEGER;
    const bAssemblyIdx = assemblyIndex.get(bAssemblyId) ?? Number.MAX_SAFE_INTEGER;
    if (aAssemblyIdx !== bAssemblyIdx) return aAssemblyIdx - bAssemblyIdx;

    const aPoiIdx = poiIndexByAssembly.get(aAssemblyId)?.get(extractPoiId(a));
    const bPoiIdx = poiIndexByAssembly.get(bAssemblyId)?.get(extractPoiId(b));
    const aFinal = aPoiIdx ?? Number.MAX_SAFE_INTEGER;
    const bFinal = bPoiIdx ?? Number.MAX_SAFE_INTEGER;
    return aFinal - bFinal;
  };
};

export const sortReviewActionPois = <T extends PoiLike>(
  reviewActionPois: unknown,
  assemblyOrder: string[],
  poiOrder: Record<string, string[]>
): Record<string, T[]> => {
  if (!reviewActionPois || typeof reviewActionPois !== 'object') return {};

  const compare = buildPoiComparator(assemblyOrder, poiOrder);

  const result: Record<string, T[]> = {};
  for (const [reviewAction, pois] of Object.entries(reviewActionPois as Record<string, T[]>)) {
    if (!Array.isArray(pois) || pois.length === 0) {
      result[reviewAction] = [];
      continue;
    }
    result[reviewAction] = [...pois].sort(compare);
  }

  return result;
};

export const sortPointOfInterests = <T extends PoiLike>(
  pointOfInterests: T[] | null | undefined,
  assemblyOrder: string[],
  poiOrder: Record<string, string[]>
): T[] => {
  if (!Array.isArray(pointOfInterests) || pointOfInterests.length === 0) return [];
  const compare = buildPoiComparator(assemblyOrder, poiOrder);
  return [...pointOfInterests].sort(compare);
};
